import { useState, useEffect, useCallback } from 'react';

// ===== Types =====

export interface UserData {
  user_id: number;
  first_name: string;
  username: string;
  language_code?: string;
  expires_at: string | null;
  days_left: number;
  days_registered: number;
  subscription: SubscriptionData | null;
  referral_count: number;
  referral_balance_kopecks?: number;
  is_referral_partner?: boolean;
  partner_percentage?: number | null;
  created_at: string | null;
  is_admin?: boolean;
  email?: string | null;
}

export interface ReferralEarningData {
  id: number;
  from_user_id: number;
  amount_kopecks: number;
  earning_type: string;
  created_at: string | null;
}

export interface ReferralStatsData {
  total_referrals: number;
  active_referrals: number;
  balance_kopecks: number;
  total_earned_kopecks: number;
  bonus_months: number;
  is_partner: boolean;
  partner_percentage: number | null;
  referral_link: string;
  earnings: ReferralEarningData[];
  withdrawal_min_rub: number;
  ref_reward_percent: number;
}

export interface ReferralUserData {
  user_id: number;
  first_name: string;
  username: string | null;
  joined_at: string | null;
  has_active_sub: boolean;
  earned_from_kopecks: number;
  bonus_months: number;
}

export interface SubscriptionData {
  id: number;
  key_id: string;
  sub_url: string;
  happ_link?: string;
  redirect_base_url?: string;
  vless_key?: string;
  server_ip: string;
  is_active: boolean;
  status: 'active' | 'inactive';
  created_at: string | null;
  expires_at?: string | null;
  days_left?: number;
  data_used_bytes?: number;
  data_limit_bytes?: number;
  online_at?: string;
}

export interface SubscriptionResponse {
  status: 'active' | 'inactive';
  subscription: SubscriptionData | null;
  server_online?: boolean;
  devices_connected?: number;
  devices_max?: number;
}

export interface GroupSubscriptionStatus {
  group: string;
  is_subscribed: boolean;
  bonus_received: boolean;
  bonus_awarded_now: boolean;
  bonus_days: number;
}

// ===== API Client =====

function getInitData(): string {
  const tg = window.Telegram?.WebApp;
  return tg?.initData || '';
}

async function apiFetch<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const initData = getInitData();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (initData) {
    headers['Authorization'] = `tma ${initData}`;
  } else {
    // Web auth fallback — use JWT from localStorage
    const jwt = localStorage.getItem('hits_jwt');
    if (jwt) {
      headers['Authorization'] = `Bearer ${jwt}`;
    }
  }

  const response = await fetch(endpoint, {
    ...options,
    headers: { ...headers, ...options?.headers },
  });

  if (!response.ok) {
    throw new Error(`API error: ${response.status}`);
  }

  return response.json();
}

export async function sendHeartbeat(): Promise<void> {
  try {
    await apiFetch<{ ok: boolean }>('/app-api/heartbeat', { method: 'POST' });
  } catch {
    // silently ignore — heartbeat is best-effort
  }
}

export async function setUserLanguage(language: 'ru' | 'en'): Promise<{ ok: boolean; language_code: string }> {
  return apiFetch<{ ok: boolean; language_code: string }>('/app-api/user/language', {
    method: 'POST',
    body: JSON.stringify({ language }),
  });
}

export async function setUserNotifications(enabled: boolean): Promise<{ ok: boolean; notifications_enabled: boolean }> {
  return apiFetch<{ ok: boolean; notifications_enabled: boolean }>('/app-api/user/notifications', {
    method: 'POST',
    body: JSON.stringify({ enabled }),
  });
}

export async function getUserNotifications(): Promise<{ notifications_enabled: boolean }> {
  return apiFetch<{ notifications_enabled: boolean }>('/app-api/user/notifications');
}

// ===== Hooks =====

export function useUserData() {
  const [data, setData] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await apiFetch<UserData>('/app-api/user');
      setData(result);
    } catch (e) {
      console.error('Failed to fetch user data:', e);
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { data, loading, error, refetch: fetch };
}

export function useSubscription() {
  const [data, setData] = useState<SubscriptionResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await apiFetch<SubscriptionResponse>('/app-api/subscription');
      setData(result);
    } catch (e) {
      console.error('Failed to fetch subscription:', e);
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetch();
  }, [fetch]);

  useEffect(() => {
    const id = window.setInterval(() => {
      fetch();
    }, 10000);
    return () => window.clearInterval(id);
  }, [fetch]);

  return { data, loading, error, refetch: fetch };
}

export function useGroupSubscription() {
  const [data, setData] = useState<GroupSubscriptionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await apiFetch<GroupSubscriptionStatus>('/app-api/group/status');
      setData(result);
    } catch (e) {
      console.error('Failed to fetch group subscription status:', e);
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { data, loading, error, refetch: fetch };
}

export function useReferralStats() {
  const [data, setData] = useState<ReferralStatsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await apiFetch<ReferralStatsData>('/app-api/referral/stats');
      setData(result);
    } catch (e) {
      console.error('Failed to fetch referral stats:', e);
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return { data, loading, error, refetch: fetchStats };
}

export function useReferralList() {
  const [data, setData] = useState<ReferralUserData[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchList = useCallback(async () => {
    try {
      setLoading(true);
      const result = await apiFetch<{ referrals: ReferralUserData[] }>('/app-api/referral/list');
      setData(result.referrals);
    } catch (e) {
      console.error('Failed to fetch referral list:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  return { data, loading, refetch: fetchList };
}

/** Send a heartbeat immediately on mount and every 30 s while the mini app is open. */
export function useHeartbeat() {
  useEffect(() => {
    sendHeartbeat();
    const id = window.setInterval(sendHeartbeat, 30_000);
    return () => window.clearInterval(id);
  }, []);
}
